import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { aiChat, AiSpinner } from "./AIHelper";
import {
  collection, doc, onSnapshot, orderBy, query,
  serverTimestamp, setDoc, deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";

// ─── Constants ────────────────────────────────────────────────────────────────
const CAT1_OPTIONS = ["General","Drawing","RFI","VO","Claim","Instruction","NCR","Programme","Quality","Commercial","Safety"];
const FLAG_COLORS = {
  NCR:         { bg:"#450a0a", border:"#ef4444", text:"#fca5a5" },
  Cost:        { bg:"#451a03", border:"#f59e0b", text:"#fcd34d" },
  RFI:         { bg:"#0c1a40", border:"#3b82f6", text:"#93c5fd" },
  VO:          { bg:"#2d1b69", border:"#8b5cf6", text:"#c4b5fd" },
  Instruction: { bg:"#052e16", border:"#22c55e", text:"#86efac" },
};
const STATUS_PILL = {
  Open:     { bg:"#052e16", border:"#22c55e", text:"#86efac" },
  Recorded: { bg:"#0c1a40", border:"#3b82f6", text:"#93c5fd" },
  Closed:   { bg:"#1e293b", border:"#475569", text:"#64748b" },
  Overdue:  { bg:"#450a0a", border:"#ef4444", text:"#fca5a5" },
  DueSoon:  { bg:"#422006", border:"#f59e0b", text:"#fcd34d" },
};
const HUB_PALETTE = [
  "#0EA5E9","#818CF8","#34D399","#FB923C","#F472B6",
  "#A78BFA","#38BDF8","#FCD34D","#4ADE80","#F87171",
  "#60A5FA","#FBBF24","#2DD4BF","#E879F9","#F97316",
];
const FONT = 12;
// Node radii
const HUB_R    = 46;
const LEAF_R   = 28;
const CENTRE_R = 38;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const todayIso = () => new Date().toISOString().slice(0,10);
const addDays  = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate()+n); return d.toISOString().slice(0,10); };
const daysDiff = (a,b) => Math.ceil((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`))/86400000);
function attentionState(item) {
  if (item.status==="Closed") return "Closed";
  if (!item.dueDate) return "Open";
  const d = daysDiff(todayIso(), item.dueDate);
  if (d < 0) return "Overdue";
  if (d <= 2) return "DueSoon";
  return "Open";
}
const inputSt = { background:"#0f172a", border:"1px solid #1e3a5f", color:"#f1f5f9", borderRadius:8, padding:"9px 12px", fontSize:13, width:"100%", outline:"none", fontFamily:"inherit", boxSizing:"border-box" };
const labelSt = { fontSize:10, color:"#64748b", textTransform:"uppercase", letterSpacing:1.5, marginBottom:5, display:"block" };
function flagPill(c) {
  return { background:c?.bg||"#1e293b", border:`1px solid ${c?.border||"#334155"}`, color:c?.text||"#94a3b8", borderRadius:4, padding:"2px 7px", fontSize:10, fontWeight:700, whiteSpace:"nowrap" };
}

function parseTaskLinks(value) {
  return String(value || "")
    .split(/[;,]/)
    .map(v => v.trim())
    .filter(Boolean);
}

function normaliseIsoDate(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  try { return new Date(value).toISOString().slice(0, 10); }
  catch { return ""; }
}

function buildProgrammeTask(item) {
  const baselineStart = normaliseIsoDate(item.baselineStart || item.plannedStart || item.receivedDate);
  const baselineEnd = normaliseIsoDate(item.baselineEnd || item.plannedEnd || item.dueDate || item.receivedDate);
  const actualStart = normaliseIsoDate(item.actualStart);
  const actualFinish = normaliseIsoDate(item.actualFinish);
  const predecessors = parseTaskLinks(item.predecessors || item.predecessor || item.pred);
  const successors = parseTaskLinks(item.successors || item.successor || item.succ);
  const isCritical = Boolean(item.isCritical || item.criticalPath || item.category1 === "Programme" || attentionState(item) === "Overdue");
  return {
    ...item,
    taskId: item.taskId || item.itemNo || item.id,
    taskName: item.taskName || item.description || item.element || "Untitled task",
    baselineStart,
    baselineEnd,
    actualStart,
    actualFinish,
    predecessors,
    successors,
    isCritical,
  };
}

function taskDateRange(tasks) {
  const dates = tasks
    .flatMap(t => [t.baselineStart, t.baselineEnd, t.actualStart, t.actualFinish])
    .filter(Boolean)
    .map(d => new Date(`${d}T00:00:00`).getTime())
    .filter(Number.isFinite);
  if (!dates.length) {
    const today = new Date(`${todayIso()}T00:00:00`).getTime();
    return { min: today, max: today + 86400000 * 30 };
  }
  const min = Math.min(...dates);
  const max = Math.max(...dates);
  return { min, max: max === min ? min + 86400000 * 7 : max };
}

function taskBarStyle(startIso, endIso, range, fallbackColor = "#3b82f6") {
  if (!startIso && !endIso) return { display: "none" };
  const start = new Date(`${startIso || endIso}T00:00:00`).getTime();
  const end = new Date(`${endIso || startIso}T00:00:00`).getTime();
  const total = Math.max(range.max - range.min, 86400000);
  const left = ((Math.min(start, end) - range.min) / total) * 100;
  const width = Math.max(((Math.abs(end - start) || 86400000) / total) * 100, 1.2);
  return {
    position: "absolute",
    left: `${Math.max(0, Math.min(99, left))}%`,
    width: `${Math.min(100 - Math.max(0, left), width)}%`,
    background: fallbackColor,
    borderRadius: 999,
    height: 8,
  };
}

// ─── Neural lattice helpers ───────────────────────────────────────────────────
function repelNodes(nodes, minDist=140, iterations=80) {
  const out = nodes.map(n=>({...n}));
  for (let iter=0; iter<iterations; iter++) {
    for (let i=0; i<out.length; i++) {
      for (let j=i+1; j<out.length; j++) {
        const dx=out[j].x-out[i].x, dy=out[j].y-out[i].y;
        const d=Math.hypot(dx,dy)||0.01;
        if (d<minDist) {
          const push=(minDist-d)/2, nx=(dx/d)*push, ny=(dy/d)*push;
          if (!out[i].fixed){out[i].x-=nx;out[i].y-=ny;}
          if (!out[j].fixed){out[j].x+=nx;out[j].y+=ny;}
        }
      }
    }
  }
  return out;
}

function placeLeaves(hub, leafIds, allNodes, leafRadius=195, minDist=110) {
  const placed=[], occupied=[...allNodes];
  leafIds.forEach((id,i)=>{
    const baseAngle=(2*Math.PI*i)/Math.max(leafIds.length,1)-Math.PI/2;
    let r=leafRadius, angle=baseAngle, tries=0, x, y;
    do {
      x=hub.x+Math.cos(angle)*r; y=hub.y+Math.sin(angle)*r;
      const clash=occupied.some(n=>Math.hypot(n.x-x,n.y-y)<minDist);
      if (!clash) break;
      angle+=0.35; if(tries%8===7) r+=30; tries++;
    } while(tries<60);
    const node={id,x,y}; placed.push(node); occupied.push(node);
  });
  return placed;
}

// ─── Neural SVG sub-components ────────────────────────────────────────────────
function NeuralLine({ x1, y1, x2, y2, color, opacity=0.35, pulse=false, delay=0 }) {
  const id = useRef(`nl-${Math.random().toString(36).slice(2)}`).current;
  const mx = (x1+x2)/2+(Math.random()-0.5)*30;
  const my = (y1+y2)/2+(Math.random()-0.5)*30;
  return (
    <g>
      <defs>
        <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor={color} stopOpacity="0"/>
          <stop offset="50%"  stopColor={color} stopOpacity={opacity}/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={`M${x1},${y1} Q${mx},${my} ${x2},${y2}`} fill="none"
        stroke={`url(#${id})`} strokeWidth={pulse?1.5:1}
        style={pulse?{animation:`neuralPulse 2.4s ease-in-out infinite`,animationDelay:`${delay}s`}:{}}/>
    </g>
  );
}

function GlowNode({ x, y, r, color, intensity=0.12 }) {
  return (
    <>
      <circle cx={x} cy={y} r={r*2.2} fill={color} opacity={intensity*0.3}/>
      <circle cx={x} cy={y} r={r*1.5} fill={color} opacity={intensity*0.5}/>
      <circle cx={x} cy={y} r={r*1.1} fill={color} opacity={intensity*0.7}/>
    </>
  );
}

function Starfield({ width, height, count=180 }) {
  const stars = useMemo(()=>Array.from({length:count},(_,i)=>({
    key:i, cx:Math.random()*width, cy:Math.random()*height,
    r:Math.random()*1.2+0.3, op:Math.random()*0.5+0.1,
  })),[width,height,count]);
  return <g>{stars.map(s=><circle key={s.key} cx={s.cx} cy={s.cy} r={s.r} fill="white" opacity={s.op}/>)}</g>;
}

// ─── AI Classification ────────────────────────────────────────────────────────
async function classifyWithAI(emailText, receivedDate, workingArea, element) {
  const sys = `You are a construction project correspondence analyst. Return ONLY a JSON object — no markdown, no explanation.
{
  "category1": one of [General,Drawing,RFI,VO,Claim,Instruction,NCR,Programme,Quality,Commercial,Safety],
  "category2": one of [Review,For Information,For Action,Approval,Clarification,Submission,Response Required,Record Only],
  "description": concise 4-8 word description,
  "summary": 1-2 sentence summary of the key issue,
  "actionRequired": what the recipient needs to do or decide,
  "costImplication": true if money/pricing/costs mentioned else false,
  "defect": true if something built/fabricated incorrectly else false,
  "reference": any drawing/document codes found or empty string,
  "sender": name of person who signed off the email,
  "reviewDays": 7 if urgent action, 14 if review, 0 if record only,
  "tags": comma-separated keywords,
  "linkedType": one of [RFI,VO,NCR,Instruction,None],
  "generatedItemTitle": short follow-up action title,
  "generatedItemDescription": one sentence describing the action needed
}
RULES:
- Wrong fabrication/build → NCR + defect:true
- Costs/pricing → VO or Commercial
- Decision needed → For Action or Response Required
- Drawing attached for review → Drawing
- Rand amounts (R, ZAR) → strong VO/Commercial indicator`;
  const data = await aiChat(sys, `Working area: ${workingArea}\nElement: ${element}\nReceived: ${receivedDate}\n\nEmail:\n${emailText}`);
  const text = (data.content||[]).map(b=>b.text||"").join("");
  return JSON.parse(text.replace(/```json|```/g,"").trim());
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ item }) {
  const state=attentionState(item), s=STATUS_PILL[state]||STATUS_PILL.Open;
  return <span style={{ background:s.bg, border:`1px solid ${s.border}`, color:s.text, borderRadius:999, padding:"3px 10px", fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>{state}</span>;
}

// ─── Correspondence Table Modal ───────────────────────────────────────────────
function CorrespondenceTableModal({ items, location, element, onClose, onEdit, onDelete, onStatusChange }) {
  const [fromDate,setFromDate]=useState(""); const [toDate,setToDate]=useState(""); const [search,setSearch]=useState("");
  const filtered = items.filter(i=>{
    if(fromDate&&i.receivedDate<fromDate) return false;
    if(toDate&&i.receivedDate>toDate) return false;
    if(search){const s=search.toLowerCase(); if(![i.description,i.summary,i.sender,i.tags,i.actionRequired].join(" ").toLowerCase().includes(s)) return false;}
    return true;
  }).sort((a,b)=>(a.receivedDate||"").localeCompare(b.receivedDate||""));
  const overdue=filtered.filter(i=>attentionState(i)==="Overdue").length;
  const totalCost=filtered.filter(i=>i.costImplication).length;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(2,5,14,0.96)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:4000, backdropFilter:"blur(14px)" }}>
      <div style={{ background:"#0a1120", borderRadius:20, border:"1px solid #1e3a5f", width:"min(1100px,97vw)", maxHeight:"92vh", display:"flex", flexDirection:"column", boxShadow:"0 0 80px rgba(14,165,233,0.12)" }}>
        <div style={{ padding:"18px 24px", borderBottom:"1px solid #1e3a5f", background:"#06101f", display:"flex", justifyContent:"space-between", alignItems:"center", borderRadius:"20px 20px 0 0" }}>
          <div>
            <div style={{ fontSize:10, color:"#3b82f6", textTransform:"uppercase", letterSpacing:3, marginBottom:4 }}>Correspondence · {location}</div>
            <div style={{ fontSize:20, fontWeight:800, color:"#f1f5f9" }}>{element}</div>
            <div style={{ display:"flex", gap:16, marginTop:6 }}>
              {[{l:`${filtered.length} Items`,c:"#3b82f6"},{l:`${overdue} Overdue`,c:"#ef4444"},{l:`${totalCost} Cost flags`,c:"#f59e0b"}].map(s=>(
                <span key={s.l} style={{ fontSize:12, color:s.c, fontWeight:700 }}>{s.l}</span>
              ))}
            </div>
          </div>
          <button onClick={onClose} style={{ background:"#1e293b", border:"1px solid #334155", color:"#94a3b8", borderRadius:10, padding:"8px 18px", cursor:"pointer", fontSize:12, fontWeight:600 }}>← Back to Map</button>
        </div>
        <div style={{ padding:"12px 24px", borderBottom:"1px solid #1e3a5f", display:"flex", gap:10, flexWrap:"wrap", alignItems:"center", background:"#06101f" }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{ ...inputSt, maxWidth:220 }}/>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}><span style={{ ...labelSt, margin:0 }}>From</span><input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{ ...inputSt, width:"auto" }}/></div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}><span style={{ ...labelSt, margin:0 }}>To</span><input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} style={{ ...inputSt, width:"auto" }}/></div>
          <button onClick={()=>{setFromDate("");setToDate("");setSearch("");}} style={{ background:"transparent", border:"1px solid #1e3a5f", color:"#64748b", borderRadius:8, padding:"8px 12px", cursor:"pointer", fontSize:12 }}>Clear</button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"0 24px 24px" }}>
          {filtered.length===0?<div style={{ textAlign:"center", padding:60, color:"#475569" }}>No correspondence matches.</div>:(
            <table style={{ width:"100%", borderCollapse:"collapse", marginTop:16 }}>
              <thead><tr style={{ borderBottom:"2px solid #1e3a5f" }}>
                {["#","Received","Due","Description","Category","Summary","Action Required","Flags","Status",""].map(h=>(
                  <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, color:"#475569", textTransform:"uppercase", letterSpacing:1.2, whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filtered.map((item,idx)=>{
                  const state=attentionState(item);
                  const rowBg=state==="Overdue"?"rgba(239,68,68,0.05)":state==="DueSoon"?"rgba(245,158,11,0.05)":idx%2===0?"#06101f":"transparent";
                  return (
                    <tr key={item.id} style={{ background:rowBg, borderBottom:"1px solid #0d1f38" }}>
                      <td style={{ padding:"10px 12px", fontSize:11, color:"#475569" }}>{item.itemNo}</td>
                      <td style={{ padding:"10px 12px", fontSize:12, whiteSpace:"nowrap", color:"#94a3b8" }}>{item.receivedDate}</td>
                      <td style={{ padding:"10px 12px", fontSize:12, whiteSpace:"nowrap" }}>
                        <div style={{ color:state==="Overdue"?"#f87171":state==="DueSoon"?"#fcd34d":"#64748b" }}>{item.dueDate||"—"}</div>
                        {state==="Overdue"&&<div style={{ fontSize:9, color:"#f87171", fontWeight:800 }}>OVERDUE</div>}
                      </td>
                      <td style={{ padding:"10px 12px", maxWidth:200 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#f1f5f9", lineHeight:1.3 }}>{item.description}</div>
                        <div style={{ fontSize:11, color:"#475569", marginTop:2 }}>{item.sender}</div>
                        {item.reference&&<div style={{ fontSize:10, color:"#3b82f6" }}>Ref: {item.reference}</div>}
                      </td>
                      <td style={{ padding:"10px 12px", whiteSpace:"nowrap" }}>
                        <div style={{ fontSize:12, color:"#93c5fd", fontWeight:600 }}>{item.category1}</div>
                        <div style={{ fontSize:10, color:"#475569" }}>{item.category2}</div>
                      </td>
                      <td style={{ padding:"10px 12px", maxWidth:220 }}><div style={{ fontSize:12, color:"#cbd5e1", lineHeight:1.5 }}>{item.summary}</div></td>
                      <td style={{ padding:"10px 12px", maxWidth:180 }}><div style={{ fontSize:12, color:"#94a3b8", lineHeight:1.5 }}>{item.actionRequired}</div></td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                          {item.defect&&<span style={{ ...flagPill(FLAG_COLORS.NCR) }}>⚠ NCR</span>}
                          {item.costImplication&&<span style={{ ...flagPill(FLAG_COLORS.Cost) }}>💰 Cost</span>}
                          {item.linkedType&&item.linkedType!=="None"&&<span style={{ ...flagPill(FLAG_COLORS[item.linkedType]||FLAG_COLORS.RFI) }}>{item.linkedType}</span>}
                        </div>
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                          <StatusBadge item={item}/>
                          <select value={item.status} onChange={e=>onStatusChange(item,e.target.value)} style={{ ...inputSt, fontSize:11, padding:"4px 8px", width:"auto" }}>
                            <option>Open</option><option>Recorded</option><option>Closed</option>
                          </select>
                        </div>
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          <button onClick={()=>onEdit(item)} style={{ background:"transparent", border:"1px solid #1e3a5f", color:"#64748b", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11, fontWeight:600 }}>✎ Edit</button>
                          <button onClick={()=>{if(window.confirm(`Delete item ${item.itemNo}?`))onDelete(item);}} style={{ background:"transparent", border:"1px solid #7f1d1d", color:"#f87171", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11 }}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Neural Mind Map ──────────────────────────────────────────────────────────
function UniverseMindMap({ correspondence, onSelectElement, onAddCorrespondence, onViewRegister, onOpenProgramme, currentProject, stats, projectClaims, fmtCurrency, totalClaimCostClaimed, totalClaimCostAwarded }) {

  const tree = useMemo(()=>{
    const t={};
    correspondence.forEach(item=>{
      const loc=item.workingArea||"General";
      const el=item.element||item.category3||"Other";
      if(!t[loc]) t[loc]={};
      if(!t[loc][el]) t[loc][el]=[];
      t[loc][el].push(item);
    });
    return t;
  },[correspondence]);

  const locations = Object.keys(tree);

  const CANVAS_W = Math.max(2400, window.innerWidth*2);
  const CANVAS_H = Math.max(2000, window.innerHeight*2);
  const CX = CANVAS_W/2, CY = CANVAS_H/2;

  // Hub layout with repulsion
  const hubNodes = useMemo(()=>{
    const raw = locations.map((loc,i)=>{
      const angle=(2*Math.PI*i)/Math.max(locations.length,1)-Math.PI/2;
      const r=locations.length===1?0:Math.min(CX,CY)*0.28;
      return { id:loc, x:CX+Math.cos(angle)*r, y:CY+Math.sin(angle)*r, color:HUB_PALETTE[i%HUB_PALETTE.length], fixed:false };
    });
    const centre={id:"__centre__", x:CX, y:CY, fixed:true};
    return repelNodes([centre,...raw],140).filter(n=>n.id!=="__centre__");
  },[locations,CX,CY]);

  // ── Focus state ─────────────────────────────────────────────────────────────
  // When a hub is focused:
  //  • The SVG pans so the hub is screen-centred
  //  • Hub renders at 2× size, leaves at 2× size
  //  • All other nodes are blurred out via a CSS overlay
  const [focusedHub,      setFocusedHub]      = useState(null);
  const [leafNodes,       setLeafNodes]       = useState([]);
  const [animatingLeaves, setAnimatingLeaves] = useState(false);

  const FOCUS_HUB_R  = HUB_R  * 2;
  const FOCUS_LEAF_R = LEAF_R * 2;
  const FOCUS_LEAF_DIST = 300;

  useEffect(()=>{
    if(!focusedHub){setLeafNodes([]);return;}
    const hub=hubNodes.find(h=>h.id===focusedHub);
    if(!hub) return;
    const els=Object.keys(tree[focusedHub]||{});
    const n=els.length;
    const ctnr=containerRef.current;
    const vw=ctnr?.clientWidth||window.innerWidth;
    const vh=ctnr?.clientHeight||window.innerHeight;
    // After panToHub the hub is screen-centre at scale=1.
    // Clamp leaf canvas-coords so nodes stay inside the visible window.
    const pad=FOCUS_LEAF_R+28;
    const cxMin=hub.x - vw/2  + pad;
    const cxMax=hub.x + vw/2  - pad;
    const cyMin=hub.y - vh/2  + pad + 54; // +54 for topbar
    const cyMax=hub.y + vh/2  - pad;
    const spread=Math.min(Math.PI*1.7, Math.max(0.5,(n-1)*0.52+0.4));
    const placed=[], occupied=[...hubNodes];
    els.forEach((id,i)=>{
      const base = n===1 ? -Math.PI/2 : -Math.PI/2 - spread/2 + (spread/Math.max(n-1,1))*i;
      let r=FOCUS_LEAF_DIST, angle=base, tries=0, x, y;
      do {
        x=hub.x+Math.cos(angle)*r;
        y=hub.y+Math.sin(angle)*r;
        x=Math.max(cxMin,Math.min(cxMax,x));
        y=Math.max(cyMin,Math.min(cyMax,y));
        const clash=occupied.some(nn=>Math.hypot(nn.x-x,nn.y-y)<FOCUS_LEAF_R*2+16);
        if(!clash) break;
        angle+=0.3; if(tries%8===7) r+=24; tries++;
      } while(tries<80);
      const node={id,x,y}; placed.push(node); occupied.push(node);
    });
    setAnimatingLeaves(true); setLeafNodes(placed);
    const t=setTimeout(()=>setAnimatingLeaves(false),700);
    return()=>clearTimeout(t);
  },[focusedHub,hubNodes,tree]);

  // Pan/zoom
  const containerRef=useRef(null);
  const [transform,setTransform]=useState({x:0,y:0,scale:0.55});
  const dragging=useRef(false), lastPos=useRef({x:0,y:0}), lastTouchDist=useRef(null);

  // Initial centre
  const centreTransform = useCallback(()=>{
    const el=containerRef.current; if(!el) return {x:0,y:0,scale:0.55};
    return {x:el.clientWidth/2-CX*0.55, y:el.clientHeight/2-CY*0.55, scale:0.55};
  },[CX,CY]);

  useEffect(()=>{ setTransform(centreTransform()); },[CX,CY,centreTransform]);

  // Smooth pan so a hub moves to the screen centre
  const panToHub = useCallback((hub)=>{
    const el=containerRef.current; if(!el) return;
    const vw=el.clientWidth, vh=el.clientHeight;
    // We want hub.x * scale + tx = vw/2  →  tx = vw/2 - hub.x * scale
    // Keep current scale (or set 1.0 for a comfortable close-up view)
    const sc=1.0;
    const tx=vw/2 - hub.x*sc;
    const ty=vh/2 - hub.y*sc;
    setTransform({x:tx, y:ty, scale:sc});
  },[]);

  const handleHubClick = useCallback((hub)=>{
    if(focusedHub===hub.id){
      // Second click = collapse back to overview
      setFocusedHub(null);
      setTransform(centreTransform());
    } else {
      setFocusedHub(hub.id);
      panToHub(hub);
    }
  },[focusedHub,panToHub,centreTransform]);

  const handleBack = useCallback(()=>{
    setFocusedHub(null);
    setTransform(centreTransform());
  },[centreTransform]);

  const onWheel=useCallback(e=>{
    e.preventDefault();
    setTransform(prev=>{
      const factor=e.deltaY<0?1.08:0.93;
      const ns=Math.min(3,Math.max(0.15,prev.scale*factor));
      const rect=containerRef.current.getBoundingClientRect();
      const mx=e.clientX-rect.left, my=e.clientY-rect.top;
      return {scale:ns, x:mx-(mx-prev.x)*(ns/prev.scale), y:my-(my-prev.y)*(ns/prev.scale)};
    });
  },[]);
  const onMouseDown=useCallback(e=>{if(e.button!==0)return;dragging.current=true;lastPos.current={x:e.clientX,y:e.clientY};},[]);
  const onMouseMove=useCallback(e=>{
    if(!dragging.current)return;
    setTransform(p=>({...p,x:p.x+(e.clientX-lastPos.current.x),y:p.y+(e.clientY-lastPos.current.y)}));
    lastPos.current={x:e.clientX,y:e.clientY};
  },[]);
  const onMouseUp=useCallback(()=>{dragging.current=false;},[]);
  const onTouchStart=useCallback(e=>{
    if(e.touches.length===1){dragging.current=true;lastPos.current={x:e.touches[0].clientX,y:e.touches[0].clientY};}
    if(e.touches.length===2) lastTouchDist.current=Math.hypot(e.touches[0].clientX-e.touches[1].clientX,e.touches[0].clientY-e.touches[1].clientY);
  },[]);
  const onTouchMove=useCallback(e=>{
    e.preventDefault();
    if(e.touches.length===1&&dragging.current){
      setTransform(p=>({...p,x:p.x+(e.touches[0].clientX-lastPos.current.x),y:p.y+(e.touches[0].clientY-lastPos.current.y)}));
      lastPos.current={x:e.touches[0].clientX,y:e.touches[0].clientY};
    }
    if(e.touches.length===2&&lastTouchDist.current){
      const d=Math.hypot(e.touches[0].clientX-e.touches[1].clientX,e.touches[0].clientY-e.touches[1].clientY);
      setTransform(p=>({...p,scale:Math.min(3,Math.max(0.15,p.scale*(d/lastTouchDist.current)))}));
      lastTouchDist.current=d;
    }
  },[]);
  const onTouchEnd=useCallback(()=>{dragging.current=false;lastTouchDist.current=null;},[]);

  const [tooltip,setTooltip]=useState(null);

  const wrapLabel = (str, max=13) => {
    const words=str.split(" "); const lines=[]; let cur="";
    words.forEach(w=>{ if(!cur){cur=w;return;} if((cur+" "+w).length<=max) cur+=" "+w; else{lines.push(cur);cur=w;} });
    if(cur) lines.push(cur); return lines;
  };

  // Focused hub object
  const focusedHubObj = focusedHub ? hubNodes.find(h=>h.id===focusedHub) : null;

  return (
    <div style={{ width:"100%", background:"#02050c", display:"flex", flexDirection:"column", fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @keyframes neuralPulse  { 0%,100%{opacity:0.2;stroke-width:1px;} 50%{opacity:0.85;stroke-width:2px;} }
        @keyframes nodeAppear   { from{transform:scale(0);opacity:0;} to{transform:scale(1);opacity:1;} }
        @keyframes focusPop     { 0%{transform:scale(0.5);opacity:0;} 60%{transform:scale(1.08);} 100%{transform:scale(1);opacity:1;} }
        .hub-node:hover .hub-circle { filter:brightness(1.35); }
        .leaf-node:hover .leaf-circle { filter:brightness(1.3); }
      `}</style>

      {/* ── Top bar ── */}
      <div style={{ padding:"9px 18px", borderBottom:"1px solid rgba(14,165,233,0.15)", background:"linear-gradient(180deg,rgba(3,8,20,0.98) 0%,rgba(2,5,12,0.92) 100%)", display:"flex", alignItems:"center", gap:8, flexShrink:0, backdropFilter:"blur(12px)", flexWrap:"wrap", position:"relative", zIndex:20 }}>

        {/* ── BACK BUTTON — top left, only shown in focus mode ── */}
        {focusedHub && (
          <button onClick={handleBack} style={{
            background:`linear-gradient(135deg,${focusedHubObj?.color}22,${focusedHubObj?.color}44)`,
            border:`1px solid ${focusedHubObj?.color}80`,
            color:"#f1f5f9", borderRadius:10, padding:"7px 16px",
            cursor:"pointer", fontSize:FONT, fontWeight:800,
            display:"flex", alignItems:"center", gap:6,
            boxShadow:`0 0 20px ${focusedHubObj?.color}30`,
          }}>
            ← Back to All Nodes
          </button>
        )}

        {!focusedHub && (
          <div style={{ marginRight:4 }}>
            <div style={{ fontSize:8, color:"#0EA5E9", textTransform:"uppercase", letterSpacing:4, marginBottom:1 }}>◈ Correspondence Universe</div>
            <div style={{ fontSize:14, fontWeight:900, color:"#f1f5f9", letterSpacing:-0.4, lineHeight:1 }}>{currentProject?.name||"Project Tracker"}</div>
          </div>
        )}

        {focusedHub && focusedHubObj && (
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:focusedHubObj.color, boxShadow:`0 0 8px ${focusedHubObj.color}` }}/>
            <div>
              <div style={{ fontSize:8, color:focusedHubObj.color, textTransform:"uppercase", letterSpacing:3 }}>Focused</div>
              <div style={{ fontSize:14, fontWeight:900, color:"#f1f5f9" }}>{focusedHub}</div>
            </div>
          </div>
        )}

        <div style={{ width:1, height:30, background:"rgba(14,165,233,0.2)", margin:"0 3px" }}/>

        <button onClick={onAddCorrespondence} style={{ background:"linear-gradient(135deg,#1d4ed8,#3b82f6)", border:"none", color:"white", borderRadius:9, padding:"7px 15px", cursor:"pointer", fontWeight:800, fontSize:FONT, boxShadow:"0 3px 14px rgba(59,130,246,0.35)", whiteSpace:"nowrap" }}>
          📨 Add Correspondence
        </button>
        <button onClick={onViewRegister} style={{ background:"rgba(99,102,241,0.14)", border:"1px solid rgba(99,102,241,0.35)", color:"#a5b4fc", borderRadius:9, padding:"7px 15px", cursor:"pointer", fontWeight:700, fontSize:FONT, whiteSpace:"nowrap" }}>
          📋 Correspondence Data
        </button>
        <button onClick={onOpenProgramme} style={{ background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.35)", color:"#fcd34d", borderRadius:9, padding:"7px 15px", cursor:"pointer", fontWeight:700, fontSize:FONT, whiteSpace:"nowrap" }}>
          📊 Programme / Gantt
        </button>

        <div style={{ width:1, height:30, background:"rgba(14,165,233,0.2)", margin:"0 3px" }}/>

        {/* Zoom */}
        {[["−",0.82],["+",1.22]].map(([lbl,f])=>(
          <button key={lbl} onClick={()=>setTransform(p=>({...p,scale:Math.min(3,Math.max(0.15,p.scale*f))}))}
            style={{ background:"rgba(14,165,233,0.1)", border:"1px solid rgba(14,165,233,0.25)", color:"#94a3b8", borderRadius:8, width:30, height:30, cursor:"pointer", fontSize:14, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center" }}>{lbl}</button>
        ))}
        <button onClick={handleBack}
          style={{ background:"rgba(14,165,233,0.1)", border:"1px solid rgba(14,165,233,0.25)", color:"#94a3b8", borderRadius:8, padding:"5px 10px", cursor:"pointer", fontSize:11, fontWeight:600 }}>⌖ Reset</button>

        <div style={{ width:1, height:30, background:"rgba(14,165,233,0.2)", margin:"0 3px" }}/>

        {/* Stats */}
        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
          {[{l:"Total",v:stats.total,c:"#0EA5E9"},{l:"Open",v:stats.open,c:"#22c55e"},{l:"Overdue",v:stats.overdue,c:"#ef4444"},{l:"Cost",v:stats.cost,c:"#f59e0b"},{l:"NCR",v:stats.ncr,c:"#a855f7"}].map(s=>(
            <div key={s.l} style={{ background:"rgba(14,165,233,0.06)", border:"1px solid rgba(14,165,233,0.11)", borderRadius:7, padding:"3px 8px", textAlign:"center" }}>
              <div style={{ fontSize:13, fontWeight:900, color:s.c, lineHeight:1 }}>{s.v}</div>
              <div style={{ fontSize:7, color:"#334155", textTransform:"uppercase", letterSpacing:1 }}>{s.l}</div>
            </div>
          ))}
          {projectClaims.length>0&&<>
            <div style={{ width:1, background:"rgba(14,165,233,0.14)", margin:"2px 0" }}/>
            {[{l:"Claims",v:projectClaims.length,c:"#fcd34d"},{l:"Claimed",v:fmtCurrency(totalClaimCostClaimed),c:"#fb923c"},{l:"Awarded",v:fmtCurrency(totalClaimCostAwarded),c:"#34d399"}].map(s=>(
              <div key={s.l} style={{ background:"rgba(14,165,233,0.06)", border:"1px solid rgba(14,165,233,0.11)", borderRadius:7, padding:"3px 8px", textAlign:"center" }}>
                <div style={{ fontSize:12, fontWeight:900, color:s.c, lineHeight:1 }}>{s.v}</div>
                <div style={{ fontSize:7, color:"#334155", textTransform:"uppercase", letterSpacing:1 }}>{s.l}</div>
              </div>
            ))}
          </>}
        </div>

        <div style={{ marginLeft:"auto", fontSize:9, color:"#1e3a5f", lineHeight:1.5, textAlign:"right" }}>
          {focusedHub ? "Click leaf to view emails · ESC or ← to go back" : "Click hub to focus · Scroll zoom · Drag pan"}
        </div>
      </div>

      {/* ── Canvas ── */}
      <div ref={containerRef}
        style={{ width:"100%", height:"calc(100vh - 108px)", overflow:"hidden", position:"relative", cursor:dragging.current?"grabbing":"grab" }}
        onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove}
        onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onClick={()=>setTooltip(null)}>

        {/* dim handled inside SVG */}

        <svg width={CANVAS_W} height={CANVAS_H} style={{
          position:"absolute",
          transform:`translate(${transform.x}px,${transform.y}px) scale(${transform.scale})`,
          transformOrigin:"0 0",
          // CSS transition for smooth pan-to-hub animation
          transition: dragging.current ? "none" : "transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94)",
        }}>
          <defs>
            <pattern id="neuralgrid" width={60} height={60} patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(14,165,233,0.025)" strokeWidth="1"/>
            </pattern>
            <pattern id="neuraldot" width={20} height={20} patternUnits="userSpaceOnUse">
              <circle cx={10} cy={10} r={0.6} fill="rgba(14,165,233,0.06)"/>
            </pattern>
            <radialGradient id="vignette" cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="transparent"/>
              <stop offset="85%"  stopColor="rgba(2,5,12,0.0)"/>
              <stop offset="100%" stopColor="rgba(2,5,12,0.7)"/>
            </radialGradient>
            <filter id="hubglow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="14" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id="hubglowBig" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="26" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id="leafglow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="7" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id="leafglowBig" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="13" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
            <filter id="centreglow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="20" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>

          {/* Background */}
          <rect width={CANVAS_W} height={CANVAS_H} fill="#02050c"/>
          <rect width={CANVAS_W} height={CANVAS_H} fill="url(#neuraldot)"/>
          <rect width={CANVAS_W} height={CANVAS_H} fill="url(#neuralgrid)"/>
          <Starfield width={CANVAS_W} height={CANVAS_H} count={180}/>
          <rect width={CANVAS_W} height={CANVAS_H} fill="url(#vignette)"/>

          {/* ── Dim rect: darkens background without blurring nodes ── */}
          {focusedHub && (
            <rect width={CANVAS_W} height={CANVAS_H} fill="rgba(2,5,12,0.80)" style={{ pointerEvents:"none" }}/>
          )}

          {/* Cross-hub lattice lines — faded when focused */}
          {hubNodes.map((a,i)=>hubNodes.slice(i+1).map(b=>(
            <NeuralLine key={`latt-${a.id}-${b.id}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              color={a.color} opacity={focusedHub ? 0.02 : 0.06}/>
          )))}

          {/* Centre → hub spokes — faded when focused */}
          {hubNodes.map((hub,hi)=>(
            <NeuralLine key={`spoke-${hub.id}`} x1={CX} y1={CY} x2={hub.x} y2={hub.y}
              color={hub.color} opacity={focusedHub && focusedHub!==hub.id ? 0.02 : 0.28}
              pulse={!focusedHub || focusedHub===hub.id} delay={hi*0.25}/>
          ))}

          {/* Hub → leaf spokes (thick, bright, only for focused hub) */}
          {focusedHub && leafNodes.map((leaf,i)=>{
            const hub=hubNodes.find(h=>h.id===focusedHub); if(!hub) return null;
            return (
              <g key={`lsp-${leaf.id}`}>
                <NeuralLine x1={hub.x} y1={hub.y} x2={leaf.x} y2={leaf.y}
                  color={hub.color} opacity={0.65} pulse delay={i*0.1}/>
              </g>
            );
          })}

          {/* Centre node — dimmed in focus mode */}
          <g style={{ cursor:"default", opacity: focusedHub ? 0.15 : 1, transition:"opacity 0.4s" }}>
            <GlowNode x={CX} y={CY} r={CENTRE_R} color="#0EA5E9" intensity={0.18}/>
            <circle cx={CX} cy={CY} r={CENTRE_R} fill="#020c1b" stroke="#0EA5E9" strokeWidth={2} filter="url(#centreglow)"/>
            <circle cx={CX} cy={CY} r={CENTRE_R} fill="none" stroke="#0EA5E9" strokeWidth={1}
              strokeDasharray="6 3" opacity={0.5} style={{ animation:"neuralPulse 3s ease-in-out infinite" }}/>
            <text x={CX} y={CY-6}  textAnchor="middle" fill="#0EA5E9" fontSize={FONT} fontWeight={900} letterSpacing={2} style={{ userSelect:"none" }}>PROJECT</text>
            <text x={CX} y={CY+9}  textAnchor="middle" fill="#38BDF8" fontSize={FONT} fontWeight={700} letterSpacing={2} style={{ userSelect:"none" }}>TRACKER</text>
            <text x={CX} y={CY+22} textAnchor="middle" fill="#1e3a5f" fontSize={10} style={{ userSelect:"none" }}>{correspondence.length} items</text>
          </g>

          {/* ── Hub nodes ── */}
          {hubNodes.map(hub=>{
            const isFocused   = focusedHub===hub.id;
            const isDimmed    = focusedHub && !isFocused;
            const R           = isFocused ? FOCUS_HUB_R : HUB_R;
            const totalItems  = Object.values(tree[hub.id]||{}).flat().length;
            const hasOverdue  = Object.values(tree[hub.id]||{}).flat().some(i=>attentionState(i)==="Overdue");
            const hasCost     = Object.values(tree[hub.id]||{}).flat().some(i=>i.costImplication);
            const elemCount   = Object.keys(tree[hub.id]||{}).length;
            const lines       = wrapLabel(hub.id, isFocused ? 14 : 13);
            const fs          = isFocused ? FONT*1.6 : FONT;
            const glowFilter  = isFocused ? "url(#hubglowBig)" : "url(#hubglow)";
            return (
              <g key={`hub-${hub.id}`} className="hub-node"
                onClick={e=>{e.stopPropagation();handleHubClick(hub);}}
                style={{ cursor:"pointer", opacity: isDimmed ? 0.08 : 1, transition:"opacity 0.4s ease" }}>
                <GlowNode x={hub.x} y={hub.y} r={R} color={hub.color} intensity={isFocused?0.28:0.1}/>
                {hasOverdue && !isDimmed &&(
                  <circle cx={hub.x} cy={hub.y} r={R+12} fill="none" stroke="#ef4444"
                    strokeWidth={isFocused?2.5:1.5} strokeDasharray="4 3" opacity={0.6}
                    style={{ animation:"neuralPulse 1.8s ease-in-out infinite" }}/>
                )}
                {isFocused&&(
                  <circle cx={hub.x} cy={hub.y} r={R+18} fill="none" stroke={hub.color}
                    strokeWidth={3} opacity={0.55} strokeDasharray="6 4"
                    style={{ animation:"neuralPulse 1.6s ease-in-out infinite" }}/>
                )}
                <circle className="hub-circle" cx={hub.x} cy={hub.y} r={R}
                  fill={isFocused?`${hub.color}22`:"#060d1a"} stroke={hub.color}
                  strokeWidth={isFocused?3:1.5} filter={glowFilter}
                  style={{ transition:"r 0.4s ease, stroke-width 0.3s ease" }}/>
                {[0.6,0.8].map((f,fi)=>(
                  <circle key={fi} cx={hub.x} cy={hub.y} r={R*f} fill="none" stroke={hub.color} strokeWidth={0.5} opacity={0.12}/>
                ))}
                <line x1={hub.x-R*0.6} y1={hub.y} x2={hub.x+R*0.6} y2={hub.y} stroke={hub.color} strokeWidth={0.6} opacity={0.18}/>
                <line x1={hub.x} y1={hub.y-R*0.6} x2={hub.x} y2={hub.y+R*0.6} stroke={hub.color} strokeWidth={0.6} opacity={0.18}/>
                {/* Count badge */}
                <circle cx={hub.x+R-8} cy={hub.y-R+8} r={isFocused?20:14} fill={hub.color} opacity={0.95}/>
                <text x={hub.x+R-8} y={hub.y-R+8+(isFocused?6:5)} textAnchor="middle" fill="white" fontSize={isFocused?15:FONT} fontWeight={900} style={{ userSelect:"none" }}>{totalItems}</text>
                {/* Element count */}
                <circle cx={hub.x-R+8} cy={hub.y-R+8} r={isFocused?15:11} fill="#0f2040" stroke={hub.color} strokeWidth={1.2} opacity={0.9}/>
                <text x={hub.x-R+8} y={hub.y-R+8+(isFocused?4:3)} textAnchor="middle" fill={hub.color} fontSize={isFocused?11:9} fontWeight={700} style={{ userSelect:"none" }}>{elemCount}</text>
                {hasCost && !isDimmed &&(
                  <circle cx={hub.x+R-8} cy={hub.y+R-8} r={isFocused?12:8} fill="#f59e0b" opacity={0.9}
                    style={{ animation:"neuralPulse 2.5s ease-in-out infinite" }}/>
                )}
                {lines.map((line,li,arr)=>(
                  <text key={li} x={hub.x} y={hub.y+(li-(arr.length-1)/2)*(fs+3)+(fs+3)*0.35}
                    textAnchor="middle" fill="white" fontSize={fs} fontWeight={800}
                    fontFamily="'DM Sans','Segoe UI',sans-serif"
                    style={{ userSelect:"none", pointerEvents:"none", letterSpacing:-0.3 }}>{line}</text>
                ))}
                {/* Hint text only when not focused and no other focus active */}
                {!focusedHub && (
                  <text x={hub.x} y={hub.y+HUB_R+15} textAnchor="middle" fill={hub.color} fontSize={8} opacity={0.4}
                    fontFamily="'DM Sans','Segoe UI',sans-serif" style={{ userSelect:"none", pointerEvents:"none" }}>click to focus</text>
                )}
              </g>
            );
          })}

          {/* ── Leaf nodes — only rendered in focus mode, 2× size ── */}
          {focusedHub && leafNodes.map((leaf,i)=>{
            const hub=hubNodes.find(h=>h.id===focusedHub); if(!hub) return null;
            const items=tree[focusedHub]?.[leaf.id]||[];
            const count=items.length;
            const hasOverdue=items.some(it=>attentionState(it)==="Overdue");
            const hasCost=items.some(it=>it.costImplication);
            const lines=wrapLabel(leaf.id,13);
            const R=FOCUS_LEAF_R;
            const fs=FONT*1.4;
            return (
              <g key={`leaf-${leaf.id}`} className="leaf-node"
                onClick={e=>{e.stopPropagation();if(onSelectElement) onSelectElement(focusedHub,leaf.id,items);}}
                onMouseEnter={e=>{
                  const rect=containerRef.current?.getBoundingClientRect(); if(!rect) return;
                  setTooltip({x:e.clientX-rect.left,y:e.clientY-rect.top,hub:focusedHub,el:leaf.id,count,hasOverdue,hasCost});
                }}
                onMouseLeave={()=>setTooltip(null)}
                style={{ cursor:"pointer", animation:animatingLeaves?`focusPop 0.5s cubic-bezier(0.34,1.56,0.64,1) ${i*0.08}s both`:"none" }}>
                <GlowNode x={leaf.x} y={leaf.y} r={R} color={hub.color} intensity={0.22}/>
                {hasOverdue&&(
                  <circle cx={leaf.x} cy={leaf.y} r={R+14} fill="none" stroke="#ef4444"
                    strokeWidth={2} strokeDasharray="4 3" opacity={0.7}
                    style={{ animation:"neuralPulse 1.8s ease-in-out infinite" }}/>
                )}
                <circle className="leaf-circle" cx={leaf.x} cy={leaf.y} r={R}
                  fill="#060d1a" stroke={hub.color} strokeWidth={2.5} filter="url(#leafglowBig)"
                  style={{ transition:"all 0.2s ease" }}/>
                {[0.6,0.8].map((f,fi)=>(
                  <circle key={fi} cx={leaf.x} cy={leaf.y} r={R*f} fill="none" stroke={hub.color} strokeWidth={0.6} opacity={0.15}/>
                ))}
                <circle cx={leaf.x} cy={leaf.y} r={6} fill={hub.color} opacity={0.75}/>
                {/* Count badge */}
                <circle cx={leaf.x+R-8} cy={leaf.y-R+8} r={16} fill={hub.color} opacity={0.95}/>
                <text x={leaf.x+R-8} y={leaf.y-R+14} textAnchor="middle" fill="white" fontSize={FONT+1} fontWeight={900} style={{ userSelect:"none" }}>{count}</text>
                {hasCost&&<circle cx={leaf.x-R+8} cy={leaf.y-R+8} r={11} fill="#f59e0b" opacity={0.92}/>}
                {/* Label inside node */}
                {lines.map((line,li,arr)=>(
                  <text key={li} x={leaf.x} y={leaf.y+(li-(arr.length-1)/2)*(fs+3)+(fs+3)*0.35}
                    textAnchor="middle" fill="white" fontSize={fs} fontWeight={700} opacity={0.95}
                    fontFamily="'DM Sans','Segoe UI',sans-serif"
                    style={{ userSelect:"none", pointerEvents:"none" }}>{line}</text>
                ))}
                {/* "tap to view" hint below node */}
                <text x={leaf.x} y={leaf.y+R+20} textAnchor="middle" fill={hub.color} fontSize={10} opacity={0.55}
                  fontFamily="'DM Sans','Segoe UI',sans-serif" style={{ userSelect:"none", pointerEvents:"none" }}>tap to view</text>
              </g>
            );
          })}
        </svg>

        {/* Tooltip — sits above the blur overlay */}
        {tooltip&&(
          <div style={{ position:"absolute", left:tooltip.x+14, top:tooltip.y-10, background:"rgba(6,13,26,0.97)", border:"1px solid rgba(14,165,233,0.3)", borderRadius:10, padding:"10px 14px", pointerEvents:"none", zIndex:50, backdropFilter:"blur(8px)", boxShadow:"0 0 24px rgba(14,165,233,0.15)", minWidth:160 }}>
            <div style={{ fontSize:9, color:"#0EA5E9", textTransform:"uppercase", letterSpacing:2, marginBottom:4 }}>{tooltip.hub}</div>
            <div style={{ fontSize:13, fontWeight:800, color:"#f1f5f9", marginBottom:6 }}>{tooltip.el}</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:10, color:"#38BDF8", fontWeight:700 }}>{tooltip.count} item{tooltip.count!==1?"s":""}</span>
              {tooltip.hasOverdue&&<span style={{ fontSize:10, color:"#f87171", fontWeight:700 }}>⚠ Overdue</span>}
              {tooltip.hasCost&&<span style={{ fontSize:10, color:"#fcd34d", fontWeight:700 }}>💰 Cost</span>}
            </div>
            <div style={{ fontSize:9, color:"#334155", marginTop:6 }}>Click to view correspondence</div>
          </div>
        )}

        {/* Mini-map */}
        {!focusedHub && (
          <div style={{ position:"absolute", bottom:16, right:16, background:"rgba(6,13,26,0.85)", border:"1px solid rgba(14,165,233,0.2)", borderRadius:10, padding:8, backdropFilter:"blur(8px)" }}>
            <svg width={120} height={80} viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}>
              <rect width={CANVAS_W} height={CANVAS_H} fill="#02050c"/>
              {hubNodes.map(hub=><circle key={hub.id} cx={hub.x} cy={hub.y} r={60} fill={hub.color} opacity={0.5}/>)}
              <circle cx={CX} cy={CY} r={40} fill="#0EA5E9" opacity={0.6}/>
              <rect x={-transform.x/transform.scale} y={-transform.y/transform.scale}
                width={window.innerWidth/transform.scale} height={window.innerHeight/transform.scale}
                fill="none" stroke="rgba(14,165,233,0.5)" strokeWidth={30}/>
            </svg>
          </div>
        )}

        {/* Empty state */}
        {correspondence.length===0&&(
          <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center", pointerEvents:"none" }}>
            <div style={{ fontSize:48, opacity:0.18, marginBottom:10 }}>◈</div>
            <div style={{ fontSize:15, color:"#1e3a5f", fontWeight:700 }}>No correspondence yet</div>
            <div style={{ fontSize:11, color:"#0f2040", marginTop:5 }}>Click "Add Correspondence" to begin</div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ padding:"6px 18px", borderTop:"1px solid rgba(14,165,233,0.08)", background:"rgba(2,5,14,0.95)", display:"flex", gap:16, alignItems:"center", flexShrink:0, flexWrap:"wrap" }}>
        <span style={{ fontSize:8, color:"#1e3a5f", textTransform:"uppercase", letterSpacing:2 }}>Legend</span>
        {[["● Hub","Location — click to focus"],["○ Leaf","Element — click for emails"],["⟳ Red","Overdue"],["● Yellow","Cost flag"]].map(([k,v])=>(
          <span key={k} style={{ fontSize:8, color:"#1e3a5f" }}><span style={{ color:"#334155" }}>{k}</span> = {v}</span>
        ))}
        <span style={{ fontSize:8, color:"#1e3a5f", marginLeft:"auto" }}>Scroll to zoom · Drag to pan</span>
      </div>
    </div>
  );
}

// ─── Programme / Gantt Pop-out ────────────────────────────────────────────────
function ProgrammeGanttModal({ tasks, currentProjectId, onClose }) {
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [highlightCritical, setHighlightCritical] = useState(true);
  const [localTasks, setLocalTasks] = useState(tasks.map(buildProgrammeTask));
  const [savingTaskId, setSavingTaskId] = useState(null);

  useEffect(() => {
    setLocalTasks(tasks.map(buildProgrammeTask));
  }, [tasks]);

  const visibleTasks = useMemo(() => {
    return criticalOnly ? localTasks.filter(t => t.isCritical) : localTasks;
  }, [criticalOnly, localTasks]);

  const range = useMemo(() => taskDateRange(visibleTasks), [visibleTasks]);

  function updateLocalTask(taskId, field, value) {
    setLocalTasks(prev => prev.map(t => t.taskId === taskId ? { ...t, [field]: value } : t));
  }

  async function saveProgrammeDates(task) {
    if (!currentProjectId || !task.id) return;
    setSavingTaskId(task.taskId);
    try {
      await setDoc(doc(db, "projects", currentProjectId, "correspondence", task.id), {
        baselineStart: task.baselineStart || "",
        baselineEnd: task.baselineEnd || "",
        predecessors: Array.isArray(task.predecessors) ? task.predecessors.join(", ") : String(task.predecessors || ""),
        successors: Array.isArray(task.successors) ? task.successors.join(", ") : String(task.successors || ""),
        actualStart: task.actualStart || "",
        actualFinish: task.actualFinish || "",
        isCritical: Boolean(task.isCritical),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      alert("Failed to save programme dates: " + e.message);
    }
    setSavingTaskId(null);
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(2,5,14,0.96)", zIndex:4600, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(14px)" }}>
      <div style={{ width:"min(1500px,98vw)", height:"min(880px,94vh)", background:"#08111f", border:"1px solid #1e3a5f", borderRadius:20, boxShadow:"0 0 80px rgba(14,165,233,0.14)", display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid #1e3a5f", background:"#06101f", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:9, color:"#0EA5E9", textTransform:"uppercase", letterSpacing:3 }}>Programme Pop-out</div>
            <div style={{ fontSize:18, fontWeight:900, color:"#f1f5f9" }}>Gantt Chart & Task Logic</div>
          </div>
          <label style={{ display:"flex", alignItems:"center", gap:7, color:"#94a3b8", fontSize:12, fontWeight:700 }}>
            <input type="checkbox" checked={criticalOnly} onChange={e=>setCriticalOnly(e.target.checked)} />
            Critical path only
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:7, color:"#94a3b8", fontSize:12, fontWeight:700, opacity:criticalOnly ? 0.45 : 1 }}>
            <input type="checkbox" checked={highlightCritical} disabled={criticalOnly} onChange={e=>setHighlightCritical(e.target.checked)} />
            Highlight critical rows
          </label>
          <button onClick={onClose} style={{ background:"#1e293b", border:"1px solid #334155", color:"#cbd5e1", borderRadius:10, padding:"8px 16px", cursor:"pointer", fontWeight:800, fontSize:12 }}>Close</button>
        </div>

        <div style={{ flex:1, overflow:"auto", padding:18 }}>
          <div style={{ minWidth:1320, display:"grid", gridTemplateColumns:"760px 1fr", gap:12, alignItems:"start" }}>
            <div style={{ border:"1px solid #102a49", borderRadius:14, overflow:"hidden", background:"#071324" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:"#06101f", borderBottom:"1px solid #1e3a5f" }}>
                    {["Task", "Baseline Start", "Baseline End", "Predecessors", "Successors", "Actual Start", "Actual Finish", "Critical", ""].map(h => (
                      <th key={h} style={{ padding:"9px 8px", textAlign:"left", color:"#64748b", fontSize:9, textTransform:"uppercase", letterSpacing:1.2, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleTasks.map((task, idx) => {
                    const criticalHighlight = !criticalOnly && highlightCritical && task.isCritical;
                    const rowBg = criticalHighlight ? "rgba(239,68,68,0.12)" : idx % 2 === 0 ? "#08182b" : "#071324";
                    return (
                      <tr key={task.id || task.taskId} style={{ background:rowBg, borderBottom:"1px solid #0d1f38" }}>
                        <td style={{ padding:"8px", minWidth:160 }}>
                          <div style={{ fontSize:12, fontWeight:800, color:criticalHighlight ? "#fca5a5" : "#f1f5f9", lineHeight:1.25 }}>{task.taskName}</div>
                          <div style={{ fontSize:10, color:"#475569" }}>#{task.taskId}</div>
                        </td>
                        <td style={{ padding:"8px" }}><input type="date" value={task.baselineStart} onChange={e=>updateLocalTask(task.taskId, "baselineStart", e.target.value)} style={{ ...inputSt, fontSize:11, padding:"5px 7px", minWidth:120 }}/></td>
                        <td style={{ padding:"8px" }}><input type="date" value={task.baselineEnd} onChange={e=>updateLocalTask(task.taskId, "baselineEnd", e.target.value)} style={{ ...inputSt, fontSize:11, padding:"5px 7px", minWidth:120 }}/></td>
                        <td style={{ padding:"8px" }}><input value={Array.isArray(task.predecessors) ? task.predecessors.join(", ") : task.predecessors || ""} onChange={e=>updateLocalTask(task.taskId, "predecessors", parseTaskLinks(e.target.value))} placeholder="e.g. 1, 2" style={{ ...inputSt, fontSize:11, padding:"5px 7px", minWidth:95 }}/></td>
                        <td style={{ padding:"8px" }}><input value={Array.isArray(task.successors) ? task.successors.join(", ") : task.successors || ""} onChange={e=>updateLocalTask(task.taskId, "successors", parseTaskLinks(e.target.value))} placeholder="e.g. 4" style={{ ...inputSt, fontSize:11, padding:"5px 7px", minWidth:95 }}/></td>
                        <td style={{ padding:"8px" }}><input type="date" value={task.actualStart} onChange={e=>updateLocalTask(task.taskId, "actualStart", e.target.value)} style={{ ...inputSt, fontSize:11, padding:"5px 7px", minWidth:120 }}/></td>
                        <td style={{ padding:"8px" }}><input type="date" value={task.actualFinish} onChange={e=>updateLocalTask(task.taskId, "actualFinish", e.target.value)} style={{ ...inputSt, fontSize:11, padding:"5px 7px", minWidth:120 }}/></td>
                        <td style={{ padding:"8px", textAlign:"center" }}><input type="checkbox" checked={task.isCritical} onChange={e=>updateLocalTask(task.taskId, "isCritical", e.target.checked)} /></td>
                        <td style={{ padding:"8px" }}>
                          <button onClick={()=>saveProgrammeDates(task)} disabled={savingTaskId===task.taskId} style={{ background:"#1d4ed8", border:"none", color:"white", borderRadius:8, padding:"6px 10px", cursor:"pointer", fontSize:11, fontWeight:800, whiteSpace:"nowrap" }}>
                            {savingTaskId===task.taskId ? "Saving…" : "Save"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {visibleTasks.length === 0 && (
                <div style={{ padding:40, textAlign:"center", color:"#475569", fontSize:13 }}>No programme tasks match the selected filter.</div>
              )}
            </div>

            <div style={{ border:"1px solid #102a49", borderRadius:14, overflow:"hidden", background:"#071324" }}>
              <div style={{ padding:"9px 12px", background:"#06101f", borderBottom:"1px solid #1e3a5f", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:10, color:"#0EA5E9", textTransform:"uppercase", letterSpacing:2 }}>Gantt Timeline</div>
                <div style={{ fontSize:10, color:"#475569" }}>{new Date(range.min).toISOString().slice(0,10)} → {new Date(range.max).toISOString().slice(0,10)}</div>
              </div>
              <div style={{ minHeight: visibleTasks.length ? visibleTasks.length * 44 : 220 }}>
                {visibleTasks.map((task, idx) => {
                  const criticalHighlight = !criticalOnly && highlightCritical && task.isCritical;
                  return (
                    <div key={`gantt-${task.id || task.taskId}`} style={{ height:44, position:"relative", borderBottom:"1px solid #0d1f38", background:criticalHighlight ? "rgba(239,68,68,0.08)" : idx % 2 === 0 ? "#08182b" : "#071324" }}>
                      <div style={{ position:"absolute", inset:"0 0 auto 0", height:"100%", background:"linear-gradient(90deg, rgba(14,165,233,0.08) 1px, transparent 1px)", backgroundSize:"10% 100%", opacity:0.6 }} />
                      <div title="Baseline" style={{ ...taskBarStyle(task.baselineStart, task.baselineEnd, range, criticalHighlight ? "#ef4444" : "#3b82f6"), top:12, opacity:0.8 }} />
                      <div title="Actual" style={{ ...taskBarStyle(task.actualStart, task.actualFinish || task.actualStart, range, "#22c55e"), top:26, opacity:0.9 }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ padding:"9px 12px", display:"flex", gap:14, borderTop:"1px solid #0d1f38", color:"#64748b", fontSize:10 }}>
                <span><span style={{ display:"inline-block", width:22, height:7, background:"#3b82f6", borderRadius:999, marginRight:6 }} />Baseline</span>
                <span><span style={{ display:"inline-block", width:22, height:7, background:"#22c55e", borderRadius:999, marginRight:6 }} />Actual</span>
                <span><span style={{ display:"inline-block", width:22, height:7, background:"#ef4444", borderRadius:999, marginRight:6 }} />Critical highlight</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────
function EditModal({ item, currentProjectId, onClose, onSaved }) {
  const [text,setText]=useState(item.rawEmail||"");
  const [date,setDate]=useState(item.receivedDate||todayIso());
  const [saving,setSaving]=useState(false);
  async function reclassify(){
    setSaving(true);
    try{
      const result=await classifyWithAI(text,date,item.workingArea,item.element);
      const dueDate=result.reviewDays>0?addDays(date,result.reviewDays):"";
      await setDoc(doc(db,"projects",currentProjectId,"correspondence",item.id),{...result,receivedDate:date,dueDate,rawEmail:text,updatedAt:serverTimestamp()},{merge:true});
      onSaved();onClose();
    }catch(e){alert("Re-classify failed: "+e.message);}
    setSaving(false);
  }
  async function saveTextOnly(){
    setSaving(true);
    await setDoc(doc(db,"projects",currentProjectId,"correspondence",item.id),{receivedDate:date,rawEmail:text,updatedAt:serverTimestamp()},{merge:true});
    onSaved();onClose();setSaving(false);
  }
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(2,5,14,0.94)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:5000, backdropFilter:"blur(10px)" }}>
      <div style={{ background:"#0a1120", borderRadius:20, border:"1px solid #1e3a5f", width:"min(680px,95vw)", maxHeight:"90vh", overflow:"auto" }}>
        <div style={{ padding:"16px 22px", borderBottom:"1px solid #1e3a5f", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:15, fontWeight:800, color:"#f1f5f9" }}>Edit Item {item.itemNo} — {item.description}</div>
          <button onClick={onClose} style={{ background:"none", border:"1px solid #1e3a5f", color:"#64748b", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontSize:12 }}>✕</button>
        </div>
        <div style={{ padding:22, display:"grid", gap:14 }}>
          <label style={{ display:"grid", gap:6 }}><span style={labelSt}>Received Date</span><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={inputSt}/></label>
          <label style={{ display:"grid", gap:6 }}><span style={labelSt}>Email Text</span><textarea value={text} onChange={e=>setText(e.target.value)} rows={12} style={{ ...inputSt, resize:"vertical", lineHeight:1.6 }}/></label>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={reclassify} disabled={saving} style={{ flex:1, background:"#1d4ed8", border:"none", color:"white", borderRadius:10, padding:11, cursor:"pointer", fontWeight:700, fontSize:13 }}>{saving?"Saving…":"🤖 Re-classify & Save"}</button>
            <button onClick={saveTextOnly} disabled={saving} style={{ background:"transparent", border:"1px solid #1e3a5f", color:"#94a3b8", borderRadius:10, padding:"11px 18px", cursor:"pointer", fontSize:13 }}>Save text only</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProjectTrackerV2({ currentProjectId, currentProject, rfis, variations, onCreateRfi, claims=[] }) {
  const [correspondence,setCorrespondence]=useState([]);

  const projectClaims=(claims||[]).filter(c=>c.projectId===currentProjectId);
  const totalClaimCostClaimed=projectClaims.reduce((sum,c)=>sum+(typeof c.pgClaimed==="number"?c.pgClaimed:parseFloat(String(c.pgClaimed||"").replace(/[^0-9.-]/g,""))||0)+(typeof c.directCostClaimed==="number"?c.directCostClaimed:parseFloat(String(c.directCostClaimed||"").replace(/[^0-9.-]/g,""))||0),0);
  const totalClaimCostAwarded=projectClaims.filter(c=>c.status==="Awarded").reduce((sum,c)=>sum+(typeof c.pgAwarded==="number"?c.pgAwarded:parseFloat(String(c.pgAwarded||"").replace(/[^0-9.-]/g,""))||0)+(typeof c.directCostAwarded==="number"?c.directCostAwarded:parseFloat(String(c.directCostAwarded||"").replace(/[^0-9.-]/g,""))||0),0);
  const totalDaysClaimed=projectClaims.reduce((sum,c)=>sum+(parseFloat(String(c.daysClaimed||""))||0),0);
  const totalDaysAwarded=projectClaims.filter(c=>c.status==="Awarded").reduce((sum,c)=>sum+(parseFloat(String(c.daysAwarded||""))||0),0);
  const fmtCurrency=v=>v>=1e6?`R${(v/1e6).toFixed(2)}M`:v>=1e3?`R${(v/1e3).toFixed(0)}K`:`R${v.toFixed(0)}`;

  // "map" is default — parent app nav controls which tab shows this component
  const [view,setView]=useState("map");
  const [step,setStep]=useState(1);
  const [workingArea,setWorkingArea]=useState("");
  const [element,setElement]=useState("");
  const [emailText,setEmailText]=useState("");
  const [receivedDate,setReceivedDate]=useState(todayIso());
  const [preview,setPreview]=useState(null);
  const [classifying,setClassifying]=useState(false);
  const [classifyError,setClassifyError]=useState("");
  const [saving,setSaving]=useState(false);
  const [editingItem,setEditingItem]=useState(null);
  const [tableView,setTableView]=useState(null);
  const [programmePopoutOpen,setProgrammePopoutOpen]=useState(false);
  const [filters,setFilters]=useState({search:"",cat1:"All",status:"All",attention:false});

  const knownLocations=[...new Set(correspondence.map(c=>c.workingArea).filter(Boolean))].sort();
  const knownElements=[...new Set(correspondence.filter(c=>c.workingArea===workingArea).map(c=>c.element).filter(Boolean))].sort();
  const allElements=[...new Set(correspondence.map(c=>c.element).filter(Boolean))].sort();

  useEffect(()=>{
    if(!currentProjectId){setCorrespondence([]);return;}
    const q=query(collection(db,"projects",currentProjectId,"correspondence"),orderBy("itemNo","asc"));
    const unsub=onSnapshot(q,snap=>setCorrespondence(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return()=>unsub();
  },[currentProjectId]);

  useEffect(()=>{
    if(!emailText.trim()||!workingArea||!element){setPreview(null);return;}
    const t=setTimeout(async()=>{
      setClassifying(true);setClassifyError("");
      try{setPreview(await classifyWithAI(emailText,receivedDate,workingArea,element));}
      catch(e){setClassifyError(`Classification failed: ${e.message}`);}
      setClassifying(false);
    },1200);
    return()=>clearTimeout(t);
  },[emailText,receivedDate,workingArea,element]);

  async function handleAdd(){
    if(!preview||!emailText.trim()||!currentProjectId) return;
    setSaving(true);
    try{
      const itemNo=correspondence.length>0?Math.max(...correspondence.map(c=>c.itemNo||0))+1:1;
      const dueDate=preview.reviewDays>0?addDays(receivedDate,preview.reviewDays):"";
      const docId=`CORR-${String(itemNo).padStart(4,"0")}`;
      await setDoc(doc(db,"projects",currentProjectId,"correspondence",docId),{
        itemNo,receivedDate,dueDate,...preview,workingArea,element,
        status:"Open",rawEmail:emailText,addedAt:serverTimestamp(),projectId:currentProjectId,id:docId,
      },{merge:true});
      setEmailText("");setPreview(null);setStep(1);setWorkingArea("");setElement("");
      setView("map");
    }catch(e){alert("Failed to save: "+e.message);}
    setSaving(false);
  }
  async function handleStatusChange(item,newStatus){
    if(!currentProjectId||!item.id) return;
    await setDoc(doc(db,"projects",currentProjectId,"correspondence",item.id),{status:newStatus,updatedAt:serverTimestamp()},{merge:true});
  }
  async function handleDelete(item){
    if(!currentProjectId||!item.id) return;
    await deleteDoc(doc(db,"projects",currentProjectId,"correspondence",item.id));
  }

  const stats={
    total:  correspondence.length,
    open:   correspondence.filter(i=>i.status==="Open").length,
    overdue:correspondence.filter(i=>attentionState(i)==="Overdue").length,
    cost:   correspondence.filter(i=>i.costImplication).length,
    ncr:    correspondence.filter(i=>i.defect||i.category1==="NCR").length,
  };
  const filtered=correspondence.filter(item=>{
    const s=filters.search.toLowerCase();
    const ms=!s||[item.description,item.summary,item.category3,item.sender,item.tags,item.workingArea,item.element].join(" ").toLowerCase().includes(s);
    const mc=filters.cat1==="All"||item.category1===filters.cat1;
    const mst=filters.status==="All"||item.status===filters.status;
    const ma=!filters.attention||["Overdue","DueSoon"].includes(attentionState(item));
    return ms&&mc&&mst&&ma;
  });

  const programmeTasks = useMemo(() => {
    return correspondence
      .filter(item => item.category1 === "Programme" || item.baselineStart || item.baselineEnd || item.actualStart || item.actualFinish || item.predecessors || item.successors)
      .map(buildProgrammeTask);
  }, [correspondence]);

  // ── Capture view ──────────────────────────────────────────────────────────
  function renderCapture(){
    return(
      <div style={{ minHeight:"100vh", background:"#060e1c", color:"#f1f5f9", fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
        <div style={{ background:"#06101f", borderBottom:"1px solid #0f2040", padding:"14px 28px", display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={()=>setView("map")} style={{ background:"rgba(14,165,233,0.1)", border:"1px solid rgba(14,165,233,0.25)", color:"#0EA5E9", borderRadius:9, padding:"7px 14px", cursor:"pointer", fontSize:FONT, fontWeight:600 }}>← Back to Map</button>
          <div>
            <div style={{ fontSize:8, color:"#0EA5E9", textTransform:"uppercase", letterSpacing:4 }}>Correspondence Universe</div>
            <div style={{ fontSize:18, fontWeight:900, color:"#f1f5f9" }}>Add Correspondence</div>
          </div>
        </div>
        <div style={{ maxWidth:880, margin:"0 auto", padding:"28px" }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:22 }}>
            {[["01","Working Area","Where?"],["02","Element","What?"],["03","Email","Paste & classify"]].map(([n,label,sub],i)=>{
              const active=step===i+1,done=step>i+1;
              return(
                <div key={n} style={{ display:"flex", alignItems:"center", flex:i<2?1:"auto" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, opacity:done||active?1:0.35 }}>
                    <div style={{ width:36,height:36,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:done?"#22c55e":active?"#3b82f6":"#1e293b",border:`2px solid ${done?"#22c55e":active?"#3b82f6":"#334155"}`,fontSize:12,fontWeight:900,color:"white",flexShrink:0 }}>{done?"✓":n}</div>
                    <div><div style={{ fontSize:FONT,fontWeight:700,color:active?"#f1f5f9":"#64748b" }}>{label}</div><div style={{ fontSize:10,color:"#334155" }}>{sub}</div></div>
                  </div>
                  {i<2&&<div style={{ flex:1,height:1,background:"#1e293b",margin:"0 12px",minWidth:16 }}/>}
                </div>
              );
            })}
          </div>

          {step===1&&(
            <div style={{ background:"#0a1628",borderRadius:18,border:"1px solid #1e3a5f",padding:28 }}>
              <div style={{ fontSize:11,color:"#3b82f6",textTransform:"uppercase",letterSpacing:3,marginBottom:6 }}>Step 01</div>
              <div style={{ fontSize:22,fontWeight:800,color:"#f1f5f9",marginBottom:4 }}>Working Area</div>
              <div style={{ fontSize:13,color:"#475569",marginBottom:20 }}>What location does this correspondence relate to?</div>
              {knownLocations.length>0&&(
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:10,color:"#0EA5E9",textTransform:"uppercase",letterSpacing:2,marginBottom:8 }}>📍 Existing locations</div>
                  <div style={{ display:"flex",gap:7,flexWrap:"wrap" }}>
                    {knownLocations.map(l=>(
                      <button key={l} onClick={()=>{setWorkingArea(l);setStep(2);}} style={{ background:workingArea===l?"#1d4ed8":"#0a1e35",border:`1px solid ${workingArea===l?"#3b82f6":"#1e3a5f"}`,color:workingArea===l?"white":"#93c5fd",borderRadius:9,padding:"8px 16px",cursor:"pointer",fontSize:FONT,fontWeight:700 }}>
                        📍 {l} <span style={{ fontSize:9,marginLeft:4,opacity:0.6 }}>{correspondence.filter(c=>c.workingArea===l).length}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <label style={labelSt}>{knownLocations.length>0?"Or add new":"Working area name"}</label>
              <div style={{ display:"flex",gap:8 }}>
                <input value={workingArea} onChange={e=>setWorkingArea(e.target.value)} onKeyDown={e=>e.key==="Enter"&&workingArea.trim()&&setStep(2)} list="kl" placeholder="e.g. Blower House, MV Substation…" style={{ ...inputSt,fontSize:14,padding:"11px 14px",flex:1 }} autoFocus={knownLocations.length===0}/>
                <button onClick={()=>setStep(2)} disabled={!workingArea.trim()} style={{ background:workingArea.trim()?"linear-gradient(135deg,#1d4ed8,#3b82f6)":"#0a1e35",border:"none",color:"white",borderRadius:11,padding:"11px 22px",cursor:workingArea.trim()?"pointer":"not-allowed",fontWeight:800,fontSize:FONT,whiteSpace:"nowrap" }}>Next →</button>
              </div>
              <datalist id="kl">{knownLocations.map(l=><option key={l} value={l}/>)}</datalist>
            </div>
          )}

          {step===2&&(
            <div style={{ background:"#0a1628",borderRadius:18,border:"1px solid #1e3a5f",padding:28 }}>
              <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:14 }}>
                <button onClick={()=>setStep(1)} style={{ background:"#0f2040",border:"1px solid #1e3a5f",color:"#64748b",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontSize:FONT }}>← Back</button>
                <div><div style={{ fontSize:9,color:"#3b82f6",textTransform:"uppercase",letterSpacing:3 }}>Step 02 · {workingArea}</div><div style={{ fontSize:18,fontWeight:800,color:"#f1f5f9" }}>Element</div></div>
              </div>
              {knownElements.length>0&&(
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:10,color:"#0EA5E9",textTransform:"uppercase",letterSpacing:2,marginBottom:8 }}>🔧 Elements in {workingArea}</div>
                  <div style={{ display:"flex",gap:7,flexWrap:"wrap" }}>
                    {knownElements.map(el=>(
                      <button key={el} onClick={()=>{setElement(el);setStep(3);}} style={{ background:element===el?"#1d4ed8":"#0a1e35",border:`1px solid ${element===el?"#3b82f6":"#1e3a5f"}`,color:element===el?"white":"#93c5fd",borderRadius:9,padding:"8px 16px",cursor:"pointer",fontSize:FONT,fontWeight:700 }}>
                        🔧 {el} <span style={{ fontSize:9,marginLeft:4,opacity:0.6 }}>{correspondence.filter(c=>c.workingArea===workingArea&&c.element===el).length}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {allElements.filter(el=>!knownElements.includes(el)).length>0&&(
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:10,color:"#334155",textTransform:"uppercase",letterSpacing:2,marginBottom:8 }}>Used elsewhere</div>
                  <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                    {allElements.filter(el=>!knownElements.includes(el)).map(el=>(
                      <button key={el} onClick={()=>{setElement(el);setStep(3);}} style={{ background:"#06101f",border:"1px solid #0f2040",color:"#334155",borderRadius:7,padding:"5px 11px",cursor:"pointer",fontSize:11,fontWeight:600 }}>{el}</button>
                    ))}
                  </div>
                </div>
              )}
              <label style={labelSt}>{knownElements.length>0?"Or add new":"Element name"}</label>
              <div style={{ display:"flex",gap:8 }}>
                <input value={element} onChange={e=>setElement(e.target.value)} onKeyDown={e=>e.key==="Enter"&&element.trim()&&setStep(3)} list="ke" placeholder="e.g. Doors, Electrical Panels, Roof…" style={{ ...inputSt,fontSize:14,padding:"11px 14px",flex:1 }} autoFocus={knownElements.length===0}/>
                <button onClick={()=>setStep(3)} disabled={!element.trim()} style={{ background:element.trim()?"linear-gradient(135deg,#1d4ed8,#3b82f6)":"#0a1e35",border:"none",color:"white",borderRadius:11,padding:"11px 22px",cursor:element.trim()?"pointer":"not-allowed",fontWeight:800,fontSize:FONT,whiteSpace:"nowrap" }}>Next →</button>
              </div>
              <datalist id="ke">{[...new Set([...knownElements,...allElements])].map(l=><option key={l} value={l}/>)}</datalist>
            </div>
          )}

          {step===3&&(
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,alignItems:"start" }}>
              <div style={{ background:"#0a1628",borderRadius:18,border:"1px solid #1e3a5f",padding:22 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:14 }}>
                  <button onClick={()=>setStep(2)} style={{ background:"#0f2040",border:"1px solid #1e3a5f",color:"#64748b",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontSize:FONT }}>← Back</button>
                  <div><div style={{ fontSize:9,color:"#3b82f6",textTransform:"uppercase",letterSpacing:2 }}>Step 03 · {workingArea} › {element}</div><div style={{ fontSize:14,fontWeight:800,color:"#f1f5f9" }}>Paste Email</div></div>
                </div>
                <label style={labelSt}>Received date</label>
                <input type="date" value={receivedDate} onChange={e=>setReceivedDate(e.target.value)} style={{ ...inputSt,marginBottom:12 }}/>
                <label style={labelSt}>Email text</label>
                <textarea value={emailText} onChange={e=>setEmailText(e.target.value)} rows={13} placeholder="Paste the full email here…" style={{ ...inputSt,resize:"vertical",lineHeight:1.6 }}/>
                <div style={{ display:"flex",gap:8,marginTop:12 }}>
                  <button onClick={handleAdd} disabled={!preview||classifying||saving||!currentProjectId} style={{ background:preview&&!classifying&&!saving&&currentProjectId?"#3b82f6":"#1e293b",border:"none",color:"white",borderRadius:11,padding:"11px 18px",cursor:preview&&!saving?"pointer":"not-allowed",fontWeight:800,fontSize:FONT,flex:1 }}>
                    {saving?"Saving…":classifying?"Analysing…":preview?"✓ Add to Register":!currentProjectId?"No project":"Paste email to begin"}
                  </button>
                  <button onClick={()=>{setEmailText("");setPreview(null);}} style={{ background:"transparent",border:"1px solid #1e3a5f",color:"#64748b",borderRadius:11,padding:"11px 14px",cursor:"pointer",fontSize:FONT }}>Clear</button>
                </div>
                {classifyError&&<div style={{ marginTop:8,color:"#f87171",fontSize:FONT }}>{classifyError}</div>}
              </div>
              <div style={{ background:"#0a1628",borderRadius:18,border:"1px solid #1e3a5f",padding:22 }}>
                <div style={{ fontSize:11,color:"#475569",textTransform:"uppercase",letterSpacing:2,marginBottom:12 }}>AI Preview</div>
                {!emailText.trim()&&<div style={{ color:"#334155",fontSize:FONT,lineHeight:1.8 }}>Paste the email on the left — AI classifies it in real time.</div>}
                {classifying&&<div style={{ display:"flex",gap:8,alignItems:"center",color:"#475569",fontSize:FONT }}><AiSpinner size={16}/> Analysing…</div>}
                {preview&&!classifying&&(
                  <div style={{ display:"grid",gap:12 }}>
                    <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                      {[preview.category1,preview.category2].filter(Boolean).map((c,i)=>(
                        <span key={i} style={{ background:i===0?"#1d4ed8":"#0f2040",border:`1px solid ${i===0?"#3b82f6":"#1e3a5f"}`,color:i===0?"#93c5fd":"#64748b",borderRadius:999,padding:"3px 10px",fontSize:11,fontWeight:700 }}>{c}</span>
                      ))}
                      {preview.defect&&<span style={{ ...flagPill(FLAG_COLORS.NCR) }}>⚠ NCR</span>}
                      {preview.costImplication&&<span style={{ ...flagPill(FLAG_COLORS.Cost) }}>💰 Cost</span>}
                    </div>
                    <div style={{ background:"#060e1c",borderRadius:8,padding:"8px 12px",border:"1px solid #0f2744" }}>
                      <div style={{ fontSize:9,color:"#1e3a5f",textTransform:"uppercase",letterSpacing:2,marginBottom:3 }}>Filing under</div>
                      <div style={{ fontSize:FONT,fontWeight:700,color:"#0EA5E9" }}>{workingArea} › {element}</div>
                    </div>
                    {[["Description",preview.description],["Sender",preview.sender||"—"],["Reference",preview.reference||"—"],["Due",preview.reviewDays>0?addDays(receivedDate,preview.reviewDays):"No review due"]].map(([k,v])=>(
                      <div key={k} style={{ display:"grid",gridTemplateColumns:"90px 1fr",gap:6 }}>
                        <div style={labelSt}>{k}</div><div style={{ fontSize:FONT,color:"#f1f5f9",fontWeight:600 }}>{v}</div>
                      </div>
                    ))}
                    <div><div style={labelSt}>Summary</div><div style={{ fontSize:FONT,color:"#94a3b8",lineHeight:1.55 }}>{preview.summary}</div></div>
                    <div><div style={labelSt}>Action Required</div><div style={{ fontSize:FONT,color:"#94a3b8",lineHeight:1.55 }}>{preview.actionRequired}</div></div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Register view ─────────────────────────────────────────────────────────
  function renderRegister(){
    return(
      <div style={{ minHeight:"100vh",background:"#060e1c",color:"#f1f5f9",fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
        <div style={{ background:"#06101f",borderBottom:"1px solid #0f2040",padding:"14px 28px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
          <button onClick={()=>setView("map")} style={{ background:"rgba(14,165,233,0.1)",border:"1px solid rgba(14,165,233,0.25)",color:"#0EA5E9",borderRadius:9,padding:"7px 14px",cursor:"pointer",fontSize:FONT,fontWeight:600 }}>← Back to Map</button>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:8,color:"#0EA5E9",textTransform:"uppercase",letterSpacing:4 }}>Correspondence Universe</div>
            <div style={{ fontSize:18,fontWeight:900,color:"#f1f5f9" }}>Correspondence Data</div>
          </div>
          <button onClick={()=>setProgrammePopoutOpen(true)} style={{ background:"rgba(245,158,11,0.12)",border:"1px solid rgba(245,158,11,0.35)",color:"#fcd34d",borderRadius:9,padding:"7px 16px",cursor:"pointer",fontWeight:800,fontSize:FONT }}>📊 Programme / Gantt</button>
          <button onClick={()=>setView("capture")} style={{ background:"linear-gradient(135deg,#1d4ed8,#3b82f6)",border:"none",color:"white",borderRadius:9,padding:"7px 16px",cursor:"pointer",fontWeight:800,fontSize:FONT,boxShadow:"0 3px 14px rgba(59,130,246,0.3)" }}>+ Add Correspondence</button>
        </div>
        <div style={{ maxWidth:1280,margin:"0 auto",padding:"22px 28px" }}>
          <div style={{ display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center" }}>
            <input value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))} placeholder="Search…" style={{ ...inputSt,maxWidth:230 }}/>
            <select value={filters.cat1} onChange={e=>setFilters(f=>({...f,cat1:e.target.value}))} style={{ ...inputSt,width:"auto" }}>
              <option value="All">All categories</option>
              {CAT1_OPTIONS.map(o=><option key={o}>{o}</option>)}
            </select>
            <select value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))} style={{ ...inputSt,width:"auto" }}>
              <option value="All">All statuses</option>
              <option>Open</option><option>Recorded</option><option>Closed</option>
            </select>
            <label style={{ display:"flex",alignItems:"center",gap:6,fontSize:FONT,color:"#475569",cursor:"pointer" }}>
              <input type="checkbox" checked={filters.attention} onChange={e=>setFilters(f=>({...f,attention:e.target.checked}))}/>
              Attention only
            </label>
            <button onClick={()=>setFilters({search:"",cat1:"All",status:"All",attention:false})} style={{ background:"transparent",border:"1px solid #1e3a5f",color:"#475569",borderRadius:7,padding:"7px 12px",cursor:"pointer",fontSize:FONT }}>Clear</button>
          </div>
          {filtered.length===0?(
            <div style={{ textAlign:"center",padding:70,color:"#1e3a5f",background:"#0a1628",borderRadius:14,border:"1px solid #0f2040" }}>
              <div style={{ fontSize:36,marginBottom:10 }}>📭</div>
              <div style={{ fontSize:14,color:"#334155" }}>No correspondence yet</div>
              <button onClick={()=>setView("capture")} style={{ marginTop:16,background:"#1d4ed8",border:"none",color:"white",borderRadius:11,padding:"10px 22px",cursor:"pointer",fontWeight:800,fontSize:FONT }}>Add your first email →</button>
            </div>
          ):(
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%",borderCollapse:"collapse",minWidth:940 }}>
                <thead><tr style={{ borderBottom:"2px solid #0f2040" }}>
                  {["#","Received","Due","Location › Element","Description","Category","Summary","Flags","Status",""].map(h=>(
                    <th key={h} style={{ padding:"9px 11px",textAlign:"left",fontSize:10,color:"#334155",textTransform:"uppercase",letterSpacing:1.2,whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filtered.map((item,idx)=>{
                    const state=attentionState(item);
                    const rowBg=state==="Overdue"?"rgba(239,68,68,0.05)":state==="DueSoon"?"rgba(245,158,11,0.05)":idx%2===0?"#0a1628":"transparent";
                    return(
                      <tr key={item.id} style={{ background:rowBg,borderBottom:"1px solid #0a1e35" }}>
                        <td style={{ padding:"9px 11px",fontSize:11,color:"#334155" }}>{item.itemNo}</td>
                        <td style={{ padding:"9px 11px",fontSize:FONT,color:"#475569",whiteSpace:"nowrap" }}>{item.receivedDate}</td>
                        <td style={{ padding:"9px 11px",fontSize:FONT,whiteSpace:"nowrap" }}>
                          <div style={{ color:state==="Overdue"?"#f87171":state==="DueSoon"?"#fcd34d":"#334155" }}>{item.dueDate||"—"}</div>
                          {state==="Overdue"&&<div style={{ fontSize:9,color:"#f87171",fontWeight:800 }}>OVERDUE</div>}
                        </td>
                        <td style={{ padding:"9px 11px" }}>
                          <div style={{ fontSize:11,color:"#0EA5E9",fontWeight:700 }}>{item.workingArea||"—"}</div>
                          <div style={{ fontSize:11,color:"#334155" }}>{item.element||"—"}</div>
                        </td>
                        <td style={{ padding:"9px 11px",maxWidth:190 }}>
                          <div style={{ fontSize:FONT,fontWeight:700,color:"#f1f5f9",lineHeight:1.3 }}>{item.description}</div>
                          <div style={{ fontSize:11,color:"#334155",marginTop:2 }}>{item.sender}</div>
                          {item.reference&&<div style={{ fontSize:10,color:"#3b82f6" }}>Ref: {item.reference}</div>}
                        </td>
                        <td style={{ padding:"9px 11px" }}>
                          <div style={{ fontSize:FONT,color:"#93c5fd",fontWeight:700 }}>{item.category1}</div>
                          <div style={{ fontSize:10,color:"#334155" }}>{item.category2}</div>
                        </td>
                        <td style={{ padding:"9px 11px",maxWidth:210 }}><div style={{ fontSize:11,color:"#64748b",lineHeight:1.5 }}>{item.summary}</div></td>
                        <td style={{ padding:"9px 11px" }}>
                          <div style={{ display:"flex",flexDirection:"column",gap:3 }}>
                            {item.defect&&<span style={{ ...flagPill(FLAG_COLORS.NCR) }}>⚠ NCR</span>}
                            {item.costImplication&&<span style={{ ...flagPill(FLAG_COLORS.Cost) }}>💰 Cost</span>}
                            {item.linkedType&&item.linkedType!=="None"&&<span style={{ ...flagPill(FLAG_COLORS[item.linkedType]||FLAG_COLORS.RFI) }}>{item.linkedType}</span>}
                          </div>
                        </td>
                        <td style={{ padding:"9px 11px" }}>
                          <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
                            <StatusBadge item={item}/>
                            <select value={item.status} onChange={e=>handleStatusChange(item,e.target.value)} style={{ ...inputSt,fontSize:11,padding:"3px 7px",width:"auto" }}>
                              <option>Open</option><option>Recorded</option><option>Closed</option>
                            </select>
                          </div>
                        </td>
                        <td style={{ padding:"9px 11px" }}>
                          <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
                            <button onClick={()=>setEditingItem(item)} style={{ background:"transparent",border:"1px solid #1e3a5f",color:"#475569",borderRadius:6,padding:"3px 9px",cursor:"pointer",fontSize:11,fontWeight:600 }}>✎ Edit</button>
                            <button onClick={()=>{if(window.confirm(`Delete item ${item.itemNo}?`))handleDelete(item);}} style={{ background:"transparent",border:"1px solid #7f1d1d",color:"#f87171",borderRadius:6,padding:"3px 9px",cursor:"pointer",fontSize:11 }}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Root render ───────────────────────────────────────────────────────────
  return (
    <>
      {view==="map"&&(
        <UniverseMindMap
          correspondence={correspondence}
          onSelectElement={(location,el,items)=>setTableView({location,element:el,items})}
          onAddCorrespondence={()=>{setStep(1);setWorkingArea("");setElement("");setEmailText("");setPreview(null);setView("capture");}}
          onViewRegister={()=>setView("register")}
          onOpenProgramme={()=>setProgrammePopoutOpen(true)}
          currentProject={currentProject}
          stats={stats}
          projectClaims={projectClaims}
          fmtCurrency={fmtCurrency}
          totalClaimCostClaimed={totalClaimCostClaimed}
          totalClaimCostAwarded={totalClaimCostAwarded}
          totalDaysClaimed={totalDaysClaimed}
          totalDaysAwarded={totalDaysAwarded}
        />
      )}
      {view==="capture"&&renderCapture()}
      {view==="register"&&renderRegister()}

      {programmePopoutOpen&&(
        <ProgrammeGanttModal
          tasks={programmeTasks.length ? programmeTasks : correspondence.map(buildProgrammeTask)}
          currentProjectId={currentProjectId}
          onClose={()=>setProgrammePopoutOpen(false)}
        />
      )}

      {tableView&&(
        <CorrespondenceTableModal
          items={tableView.items}
          location={tableView.location}
          element={tableView.element}
          onClose={()=>setTableView(null)}
          onEdit={item=>{setTableView(null);setEditingItem(item);}}
          onDelete={async item=>{await handleDelete(item);setTableView(null);}}
          onStatusChange={handleStatusChange}
        />
      )}
      {editingItem&&(
        <EditModal
          item={editingItem}
          currentProjectId={currentProjectId}
          onClose={()=>setEditingItem(null)}
          onSaved={()=>setEditingItem(null)}
        />
      )}
    </>
  );
}